import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

import { computed, inject, onBeforeUnmount, onMounted, PropType, ref } from 'vue';
import {
  CompleteLocation,
  injectionKey,
  OSDExposition,
} from '@/core/components/data-display/OSD/osd.shared';
import OpenSeadragon from 'openseadragon';
import { Inspection } from '@/entities/inspection/types';
import { useGetInspectionImageAspectRatioQuery } from '@/entities/inspection/hooks';
import store from '@/store';


export default /*#__PURE__*/_defineComponent({
  __name: 'NkOSDOverlayMeasure',
  props: {
  injectionKey: {
    type: Symbol,
    default: injectionKey,
  },
  inspection: {
    type: Number as PropType<Inspection['id']>,
    required: true,
  }
},
  setup(__props) {

const props = __props


const injected = inject<OSDExposition>(props.injectionKey);
const service = store.getters['service/service'];

const lineColor = computed(() => service === 2 ? 'white' : '#FF6D10');
const textColor = computed(() => service === 2 ? 'white' : 'black');

const inspectionImageRatioQueryData = computed(() => ({ id: props.inspection }));
const hasInspectionImageRatioQueryData = computed(() => !!inspectionImageRatioQueryData.value.id);
const { data: inspectionImageRatio } = useGetInspectionImageAspectRatioQuery(inspectionImageRatioQueryData, {
  enabled: hasInspectionImageRatioQueryData,
});

const el = ref<HTMLElement>();
const measureNumber = ref(0);
const eventsToDisable: (keyof OpenSeadragon.ViewerEventMap)[] = [ 'canvas-click', 'canvas-double-click', 'zoom', 'pan', 'rotate', 'canvas-scroll', 'canvas-drag', 'canvas-pinch' ];

const mousePosition = ref<OpenSeadragon.Point>(new OpenSeadragon.Point());
const positions = ref<[ OpenSeadragon.Point | null, OpenSeadragon.Point | null ]>([ null, null ]);

const elPositions = computed(() => {
  const minX = Math.min(positions.value[0]?.x ?? 0, mousePosition.value.x ?? 0);
  const minY = Math.min(positions.value[0]?.y ?? 0, mousePosition.value.y ?? 0);
  const maxX = Math.max(positions.value[0]?.x ?? 0, mousePosition.value.x ?? 0);
  const maxY = Math.max(positions.value[0]?.y ?? 0, mousePosition.value.y ?? 0);
  const width = maxX - minX;
  const height = maxY - minY;
  return {
    minX,
    minY,
    maxX,
    maxY,
    width,
    height,
  };
});

const elStyle = computed(() => {
  const {
    minX,
    minY,
    width,
    height,
  } = elPositions.value;
  return {
    top: `${ minY }px`,
    left: `${ minX }px`,
    width: `${ width }px`,
    height: `${ height }px`,
  };
});

let mouseTracker: OpenSeadragon.MouseTracker;

const disableEvent = (event: OpenSeadragon.ViewerEventMap[keyof OpenSeadragon.ViewerEventMap]) => {
  // eslint-disable-next-line no-param-reassign
  if ( 'preventDefaultAction' in event ) event.preventDefaultAction = true;
};
const getViewer = () => {
  if ( injected ) {
    return injected.getViewer();
  }
  return null;
};
const getLocation = computed(() => {
  const viewer = getViewer();
  if ( viewer ) {
    const viewportBounds = viewer.viewport.getBounds();
    const {
      minX,
      minY,
      width,
      height,
    } = elPositions.value;
    const positionCoordinates = viewer.viewport.deltaPointsFromPixels(new OpenSeadragon.Point(minX, minY));
    const sizeCoordinates = viewer.viewport.deltaPointsFromPixels(new OpenSeadragon.Point(width, height));

    const location = new OpenSeadragon.Rect(
      positionCoordinates.x + viewportBounds.x,
      positionCoordinates.y + viewportBounds.y,
      sizeCoordinates.x,
      sizeCoordinates.y,
    );

    const pixelLocation = viewer.viewport.viewportToImageRectangle(location);
    return {
      location,
      pixelLocation,
      viewportLocation: location,
    };
  }
  return null;
});

/**
 * Get the text transform to display the measure in the correct position.
 * @param x1
 * @param y1
 * @param x2
 * @param y2
 */
const getTextTransform = (x1: number, y1: number, x2: number, y2: number) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const angleRad = Math.atan2(dy, dx);
  let angleDeg = angleRad * 180 / Math.PI;

  if (angleDeg > 90) {
    angleDeg -= 180;
  } else if (angleDeg < -90) {
    angleDeg += 180;
  }

  const length = Math.hypot(dx, dy);

  const ux = dy / length;
  const uy = -dx / length;

  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;

  const x = midX + -14 * ux;
  const y = midY + -14 * uy;

  return {
    angle: angleDeg,
    x,
    y
  }
}

/**
 * Convert pixels distance to cm
 */
const measureToCm = computed(() => {
  if ( getLocation.value === null || inspectionImageRatio.value === undefined ) return 0;

  const measure = {
    x: (Math.floor((getLocation.value.pixelLocation.width * inspectionImageRatio.value.imageRatio) * 100) / 100),
    y: (Math.floor((getLocation.value.pixelLocation.height * inspectionImageRatio.value.imageRatio) * 100) / 100)
  };

  return Math.floor(Math.sqrt(measure.x ** 2 + measure.y ** 2) * 100);
});


const onViewerCanvasPress = (event: OpenSeadragon.ViewerEventMap['canvas-press']) => {
  positions.value[0] = event.position;

  const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  line.setAttribute('stroke', lineColor.value);
  line.setAttribute('stroke-width', '2');
  line.setAttribute('x1', positions.value[0].x.toString());
  line.setAttribute('y1', positions.value[0].y.toString());
  line.setAttribute('x2', positions.value[0].x.toString());
  line.setAttribute('y2', positions.value[0].y.toString());
  line.setAttribute('id', `measure-${ measureNumber.value }`);

  const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  text.setAttribute('id', `text-${ measureNumber.value }`);
  text.setAttribute('fill', textColor.value);
  text.setAttribute('font-size', '16');
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'central');
  text.setAttribute('x', positions.value[0].x.toString());
  text.setAttribute('y', positions.value[0].y.toString());

  const svgOverlay = document.getElementById('svg-overlay');
  if ( svgOverlay ) {
    svgOverlay.appendChild(line);
    svgOverlay.appendChild(text);
  }
};

const onViewerCanvasDrag = (event: OpenSeadragon.ViewerEventMap['canvas-drag']) => {
  if ( positions.value[0] ) {
    const line = document.getElementById(`measure-${ measureNumber.value }`);
    if ( line ) {
      line.setAttribute('x2', mousePosition.value.x.toString());
      line.setAttribute('y2', mousePosition.value.y.toString());
    }

    const text = document.getElementById(`text-${ measureNumber.value }`);
    if ( text ) {
      const {angle, x, y } = getTextTransform(positions.value[0].x, positions.value[0].y, mousePosition.value.x, mousePosition.value.y)
      text.setAttribute('x', x.toString());
      text.setAttribute('y', y.toString());
      text.setAttribute('transform', `rotate(${ angle }, ${ x }, ${ y })` );
      text.setAttribute('text-anchor', 'middle');
      text.textContent = `${ measureToCm.value.toString() } cm`;
    }
  }
};

const onViewerCanvasRelease = (event: OpenSeadragon.ViewerEventMap['canvas-release']) => {
  positions.value[1] = event.position;
  measureNumber.value += 1;
  positions.value = [ null, null ];
};

const onViewerCanvasMouseMove: OpenSeadragon.MouseTracker['moveHandler'] = (event) => {
  if ( 'position' in event ) mousePosition.value = (event as unknown as {
    position: OpenSeadragon.Point
  }).position;
};

const addEventListeners = () => {
  const viewer = getViewer();
  if ( viewer ) {
    eventsToDisable.forEach(event => {
      viewer.addHandler(event, disableEvent);
    });
    viewer.addHandler('canvas-press', onViewerCanvasPress);
    viewer.addHandler('canvas-drag', onViewerCanvasDrag);
    viewer.addHandler('canvas-release', onViewerCanvasRelease);

    const svgOverlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svgOverlay.setAttribute('id', 'svg-overlay');
    svgOverlay.style.position = 'absolute';
    svgOverlay.style.top = '0';
    svgOverlay.style.left = '0';
    svgOverlay.style.width = '100%';
    svgOverlay.style.height = '100%';
    svgOverlay.style.pointerEvents = 'none';
    viewer.container.appendChild(svgOverlay);
    mouseTracker = new OpenSeadragon.MouseTracker({
      element: viewer.canvas,
      moveHandler: onViewerCanvasMouseMove,
    });
  }
};

const removeEventListeners = () => {
  const viewer = getViewer();
  if ( viewer ) {
    eventsToDisable.forEach(event => {
      viewer.removeHandler(event, disableEvent);
    });

    viewer.removeHandler('canvas-press', onViewerCanvasPress);
    viewer.removeHandler('canvas-drag', onViewerCanvasDrag);
    viewer.removeHandler('canvas-release', onViewerCanvasRelease);

    const svgOverlay = document.getElementById('svg-overlay');
    if ( svgOverlay ) viewer.container.removeChild(svgOverlay);
    if ( mouseTracker ) mouseTracker.destroy();
  }
};

onMounted(() => {
  if ( injected ) {
    injected.isReady()
      .then(() => {
        addEventListeners();
      });
  }
});

onBeforeUnmount(async () => {
  removeEventListeners();
});

return (_ctx: any,_cache: any) => {
  return (positions.value[0])
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "el",
        ref: el,
        style: _normalizeStyle(_unref(elStyle)),
        class: "absolute h-full w-full pointer-events-none z-50"
      }, null, 4))
    : _createCommentVNode("", true)
}
}

})